import { useEffect, useSyncExternalStore } from "react";
import { useMutation, useMutationState, useQueryClient, type MutationState, type MutationFilters } from "@tanstack/react-query";
import { useBlocker } from "react-router";
import { EnvelopeError } from "@/api/errors";
import { useSessionScope } from "@/auth/use-session-scope";

interface CommandIntent<TBody> { readonly body: TBody; readonly key: string; hadUnknownOutcome: boolean }
type CommandState<TBody, TData> = MutationState<TData, unknown, CommandIntent<TBody>>;
function definitive(error: unknown): boolean {
  return error instanceof EnvelopeError && error.httpStatus >= 400 && error.httpStatus < 500;
}
function uncertain<TBody, TData>(state: CommandState<TBody, TData> | undefined): boolean {
  return state?.status === "pending" || (state?.status === "error" && (state.variables?.hadUnknownOutcome === true || !definitive(state.error)));
}

interface RetainedCommandOptions<TBody, TData> {
  readonly key: readonly string[];
  readonly execute: (body: TBody, idempotencyKey: string) => Promise<TData>;
  readonly onSuccess: (row: TData) => void;
}

/** Tek komutlu sayfa: yönlendirme ve belge kapanışı bu kancaya aittir. */
export function useRetainedCommand<TBody, TData>(options: RetainedCommandOptions<TBody, TData>) {
  const command = useRetainedCommandCore(options);
  const blocker = useBlocker(command.shouldBlockNavigation);
  useEffect(() => { if (blocker.state === "blocked") blocker.reset(); }, [blocker]);
  useEffect(() => {
    if (!command.frozen) return;
    const block = (event: BeforeUnloadEvent): void => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", block);
    return () => window.removeEventListener("beforeunload", block);
  }, [command.frozen]);
  return command;
}

/** Birden çok komut taşıyan sayfa tek router guard kurar; alt paneller yalnız niyeti yönetir. */
export function useRetainedCommandCore<TBody, TData>(options: RetainedCommandOptions<TBody, TData>) {
  const queryClient = useQueryClient();
  const mutationKey = ["retained-command", ...useSessionScope(), ...options.key];
  const onSuccess = options.onSuccess;
  const cache = queryClient.getMutationCache();
  const states = useMutationState<CommandState<TBody, TData>>({ filters: { mutationKey, exact: true }, select: (mutation) => mutation.state as CommandState<TBody, TData> });
  const state = states.at(-1);
  const command = useMutation({
    mutationKey,
    mutationFn: async (intent: CommandIntent<TBody>) => {
      try { return await options.execute(intent.body, intent.key); }
      catch (error) {
        // Sonraki 4xx yalnız o yeniden denemeyi reddeder; önceki bilinmeyen commit'i çürütemez.
        if (!definitive(error)) intent.hadUnknownOutcome = true;
        throw error;
      }
    },
    // Belirsiz yanıt kullanıcı aynı isteği tamamlayana veya oturumu kapatana kadar korunur.
    gcTime: Infinity,
    // Offline yazı kuyruğu, oturum değişince yeni bearer ile kendiliğinden çalışmamalı.
    networkMode: "always",
    retry: false,
  });
  const latest = (): CommandState<TBody, TData> | undefined => cache.findAll({ mutationKey, exact: true }).at(-1)?.state as CommandState<TBody, TData> | undefined;
  const frozen = uncertain(state);
  useEffect(() => {
    if (state?.status !== "success" || !cache.findAll({ mutationKey, exact: true }).some((entry) => entry.state.status === "success")) return;
    for (const entry of cache.findAll({ mutationKey, exact: true })) cache.remove(entry);
    // MutationState ayrık birleşim değildir; success durumunda execute TData döndürmüştür.
    onSuccess(state.data as TData);
  }, [state, cache, mutationKey, onSuccess]);
  async function send(body: TBody): Promise<void> {
    const existing = latest();
    if (existing?.status === "pending") return;
    const saved = uncertain(existing) ? existing!.variables! : { body: structuredClone(body), key: crypto.randomUUID(), hadUnknownOutcome: false };
    for (const entry of cache.findAll({ mutationKey, exact: true })) cache.remove(entry);
    // Hata görünümü cache durumundan türetilir; Promise reddi ikinci bir hata kanalı değildir.
    await command.mutateAsync(saved).catch(() => {});
  }
  function reset(): boolean {
    if (uncertain(latest())) return false;
    for (const entry of cache.findAll({ mutationKey, exact: true })) cache.remove(entry);
    return true;
  }
  return { reset, shouldBlockNavigation: () => uncertain(latest()), busy: state?.status === "pending", frozen, body: state?.variables?.body, error: state?.error ?? null, send, retry: async (): Promise<void> => { const saved = latest()?.variables; if (saved) await send(saved.body); } };
}

/** Aynı kimlik/oturumun seçilen komutlarını canlı cache'ten toplar; unmount veya şirket reset'i niyeti düşürmez. */
export function useRetainedCommandGuardState(commandNames: readonly string[]) {
  const queryClient = useQueryClient();
  const scope = useSessionScope();
  const filters: MutationFilters = {
    mutationKey: ["retained-command", ...scope],
    predicate: entry => {
      const name = entry.options.mutationKey?.[scope.length + 1];
      return typeof name === "string" && commandNames.includes(name);
    },
  };
  const isBlocked = (state: MutationState) => uncertain(state as CommandState<unknown, unknown>);
  const cache = queryClient.getMutationCache();
  const shouldBlockNavigation = () => cache.findAll(filters).some(entry => isBlocked(entry.state));
  // Snapshot her render'da yeni scope'u okur; kimlik değişirken eski filtre sonucu taşınmaz.
  const blocked = useSyncExternalStore(onChange => cache.subscribe(onChange), shouldBlockNavigation, () => false);
  return { blocked, shouldBlockNavigation };
}
